import { Flex, type ListRef, Spinner } from '@banhala/ui-kit';
import { AtomicRenderer } from 'shared/src/component';
import { useGetTodayScreensInfiniteQuery } from './queries/useGetTodayScreensInfiniteQuery';
import { StyleSheet, View } from 'react-native';
import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useRef,
} from 'react';
import type { IScreensComponent } from 'shared/src/api';
import { useScrollToFilterList } from './hooks/useScrollToFilterList';
import { HomeScrollContext } from '../../context/HomeScrollContext';
import { useIsTabFocused } from '../../hooks/useIsTabFocused';
import { useRegisterScrollToTop } from '../../hooks/useRegisterScrollToTop';
import { useLogViewToday } from './hooks/useLogViewToday';

export type AtomicRendererRefCurrent = ListRef<IScreensComponent>;

export type TodayAtomicHandle = { scrollToTop: () => void };

type TodayAtomicProps = {
  screenName: string;
  nextToken?: string;
  /**
   * 외부에서 스크롤 위치를 구독. screen 단독 호스팅 시 floating button 펼침/접힘
   * 트리거에 사용. HomeScrollContext 와 병행 호출되며 둘 다 안전하게 noop 가능.
   */
  onScrollY?: (y: number) => void;
};

const TodayAtomicImpl = (
  props: TodayAtomicProps,
  ref: React.Ref<TodayAtomicHandle>,
) => {
  const { onScrollY: onScrollYProp } = props;
  const atomicRendererRef = useRef<AtomicRendererRefCurrent>(null);
  const homeScroll = useContext(HomeScrollContext);
  const { data, fetchNextPage, isFetching, refetch } =
    useGetTodayScreensInfiniteQuery(props.screenName, props.nextToken);
  const { filterListIndex, scrollToFilterList } = useScrollToFilterList({
    atomicRendererRef,
    components: data?.components,
  });
  const focused = useIsTabFocused(props.screenName);

  useLogViewToday(props.screenName);

  const handleScrollToTop = useCallback(() => {
    atomicRendererRef.current?.scrollToOffset({ offset: 0, animated: true });
  }, []);
  useRegisterScrollToTop(props.screenName, handleScrollToTop);
  useImperativeHandle(ref, () => ({ scrollToTop: handleScrollToTop }), [
    handleScrollToTop,
  ]);

  const handleScrollY = useCallback(
    (y: number) => {
      homeScroll?.onScrollY(y);
      onScrollYProp?.(y);
    },
    [homeScroll, onScrollYProp],
  );

  if (!data) {
    return (
      <Flex.Center flex={1}>
        <Spinner size={20} />
      </Flex.Center>
    );
  }

  return (
    <Flex flex={1}>
      {isFetching && (
        <View style={styles.spinnerContainer}>
          <Spinner size={20} />
        </View>
      )}
      <AtomicRenderer
        //나중에 바꿀값 (메인 변경시)
        isForeground={true}
        refetch={refetch}
        data={data ?? []}
        screen={props.screenName}
        fetchNextPage={fetchNextPage}
        listRef={atomicRendererRef}
        filterListIndex={filterListIndex}
        scrollToFilterList={scrollToFilterList}
        isFetching={isFetching}
        onScrollY={handleScrollY}
        scrollYSharedValue={homeScroll?.scrollY}
        logDisabled={!focused}
        hideScrollToTop={true}
      />
    </Flex>
  );
};

export const TodayAtomic = forwardRef(TodayAtomicImpl);
const styles = StyleSheet.create({
  spinnerContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -10 }, { translateY: -10 }],
    zIndex: 1000,
  },
});
